/**
 * RS word reveal — shared by RS / Recente Words and RS / Blog Index.
 *
 * Progressive enhancement: text is fully visible without JS. Each
 * [data-rs-scramble] element under the given root is split into word spans
 * that rise into place the first time the element enters the viewport —
 * one-shot: once revealed, an element is unobserved and never resets.
 * Returns the observer (to disconnect on teardown).
 */
const REVEAL_THRESHOLD = 0.35;
const MAX_RANDOM_DELAY_MS = 1100;

export function setupWordReveal(root) {
  const blocks = root.querySelectorAll('[data-rs-scramble]');
  if (blocks.length === 0) return null;

  for (const el of blocks) split(el);

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        reveal(entry.target);
        observer.unobserve(entry.target);
      }
    },
    { threshold: REVEAL_THRESHOLD }
  );
  for (const el of blocks) observer.observe(el);
  return observer;
}

function split(el) {
  const words = (el.textContent || '').split(/\s+/).filter(Boolean);
  el.textContent = '';
  words.forEach((word, index) => {
    const span = document.createElement('span');
    span.textContent = word;
    span.className = 'rs-words__word';
    el.appendChild(span);
    if (index < words.length - 1) {
      // A real element, not a text node: flex containers (e.g. the
      // inline-flex read links) ignore raw text-node whitespace between
      // items, which printed READTHEARTICLE without spaces
      const gap = document.createElement('span');
      gap.className = 'rs-words__word-gap';
      gap.textContent = ' ';
      el.appendChild(gap);
    }
  });
}

function reveal(el) {
  for (const span of el.querySelectorAll('.rs-words__word')) {
    span.style.transitionDelay = `${Math.random() * MAX_RANDOM_DELAY_MS}ms`;
    span.classList.add('is-revealed');
  }
}

class RsRecenteWords extends HTMLElement {
  #observer = null;

  connectedCallback() {
    if (this.dataset.scramble !== 'true') return;
    this.#observer = setupWordReveal(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }
}

if (!customElements.get('rs-recente-words')) {
  customElements.define('rs-recente-words', RsRecenteWords);
}
